
import React from 'react';
import { Employee, EmployeeRole } from '../types';
import { Button } from './Button';

interface SidebarProps {
  activeView: 'dashboard' | 'allTicketsView' | 'customerSearch' | 'customerProfile' | 'inventory' | 'selfCheckIn' | 'labels' | 'adminUsers' | 'adminSettings' | 'adminUICustomization' | 'adminActivityLog' /*| 'adminChecklists'*/ | 'adminBuybackSettings' | 'knowledgeBase' | 'aiTroubleshooter' | 'aiDiagnosticAssistant' | 'customerPortal' | 'buybackWidget' | 'ticketBoard' | 'reports' | 'appointments' | 'permissionDenied';
  newTicketsCount: number;
  onNewTicket: () => void;
  onShowDashboard: () => void;
  onShowAllTickets: () => void;
  onShowTicketBoard: () => void;
  onShowAppointments: () => void; // New
  onShowReports: () => void;
  onShowCustomerSearch: () => void;
  onShowInventory: () => void;
  onShowSelfCheckIn: () => void;
  onShowBuybackWidget: () => void;
  onShowLabels: () => void;
  onShowAdminUsers: () => void;
  onShowAdminSettings: () => void;
  onShowAdminUICustomization: () => void;
  onShowAdminActivityLog: () => void;
  // onShowAdminChecklists: () => void; 
  onShowAdminBuybackSettings: () => void; // New
  onOpenSubmitSuggestion: () => void;
  onShowKnowledgeBase: () => void;
  onShowAITroubleshooter: () => void;
  onShowAIDiagnosticAssistant: () => void;
  currentUser: Employee | null;
}

// Icon components (SVGs)
const PlusCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const HomeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h7.5" />
  </svg>
);

const QueueIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
    </svg>
);

const ViewColumnsIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" />
  </svg>
);

const UserGroupIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-3.741-5.243M15 15.75a3 3 0 01-3-3M15 15.75a3 3 0 00-3-3m-3.75 3.75a3 3 0 01-3-3M15 15.75a3 3 0 003 3M12 12.75v-1.5m0 1.5H9.75m2.25 0a2.25 2.25 0 012.25 2.25M12 12.75v-1.5m0 1.5a2.25 2.25 0 00-2.25 2.25M12 12.75V9.75m0 3H9.75M3.75 21A3.75 3.75 0 017.5 17.25m5.25 0a3.75 3.75 0 013.75 3.75M16.5 21a3.75 3.75 0 003.75-3.75m0 0V17.25m0 0A3.75 3.75 0 0016.5 13.5m-5.25 0A3.75 3.75 0 007.5 13.5m0 0V17.25m0 0A3.75 3.75 0 003.75 21" />
  </svg>
);

const CogIconSmall: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-slate-600" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l-1.41-.513M3 10.5h18M4.5 4.5l.47.187m14.554 9.626l-.47.187M9.75 21a8.25 8.25 0 0010.607-6.75M9.75 3a8.25 8.25 0 0110.607 6.75" />
  </svg>
);

const ArchiveBoxIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10.5 11.25h3M12 15h.008" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 7.5h16.5M16.5 7.5V5.625c0-1.036-.84-1.875-1.875-1.875h-5.25C8.34 3.75 7.5 4.59 7.5 5.625V7.5" />
  </svg>
);

const ClipboardUserIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9M10.125 2.25h.375m9.375 9.375c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5c-.621 0-1.125-.504-1.125-1.125V15m1.5-1.5h5.25m0 0l-3.75-3.75M15 12h5.25M15 12V6.75" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 12h-.75a.375.375 0 01-.375-.375V6.375a.375.375 0 01.375-.375h4.5a.375.375 0 01.375.375v5.25a.375.375 0 01-.375.375h-.75M9 12h-1.5" />
 </svg>
);

const TagIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
  </svg>
);

const UserCircleIconSmall: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-slate-600" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const LightBulbIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.355a7.5 7.5 0 01-3 0m3.75-2.355a7.5 7.5 0 003 0m-3.75 2.355V18m-3.75-2.645A12.06 12.06 0 0112 15c1.407 0 2.733.283 3.904.796M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const BookOpenIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const ChatBubbleLeftRightIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3.697-3.697c-.318-.318-.686-.573-1.086-.754A47.34 47.34 0 0112 15.75c-1.348 0-2.676.153-3.964.437-.4.18-.768.436-1.086.754l-3.697 3.697V16.513a48.182 48.182 0 01-1.02-.072A2.25 2.25 0 013.75 15V10.608c0-.97.616-1.813 1.5-2.097m15 0c0-1.136-.847-2.1-1.98-2.193L12 6c-1.133 0-1.98.962-1.98 2.193m15 0A2.25 2.25 0 0019.5 7.5h0l3 3A2.25 2.25 0 0021 13.5V15" />
  </svg>
);

const CurrencyDollarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 11.219 12.768 11 12 11c-.768 0-1.536.219-2.121.782l-.879.659M7.5 14.25l6-6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PaintBrushIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-slate-600" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.385m0 0a2.25 2.25 0 012.245-2.4 3 3 0 001.128-5.78 16.05 16.05 0 00-4.773 2.267m-4.016 5.043a15.998 15.998 0 00-1.622-3.385m0 0a2.25 2.25 0 00-2.4-2.245 3 3 0 00-5.78 1.128 16.05 16.05 0 002.267 4.773m5.043-.025a15.995 15.995 0 01-3.388-1.62m0 0a15.995 15.995 0 00-3.388 1.62m6.776 0a15.995 15.995 0 001.622 3.385m0 0a2.25 2.25 0 01-2.245 2.4 3 3 0 00-1.128 5.78 16.05 16.05 0 004.773-2.267m-2.045-4.772a15.998 15.998 0 001.622 3.385" />
  </svg>
);

const BeakerIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355-.186-.676-.401-.959a1.984 1.984 0 00-1.605-1.045 1.983 1.983 0 00-1.604 1.045 1.11 1.11 0 00-.401.959v1.413h4.01V6.087z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 12.75L11.25 12.75M12.75 12.75V6.375V5.625C12.75 4.904 12.196 4.35 11.475 4.35H8.525C7.804 4.35 7.25 4.904 7.25 5.625V6.375m5.5 6.375v3.375c0 .621-.504 1.125-1.125 1.125H8.625c-.621 0-1.125-.504-1.125-1.125V12.75m5.5 0H7.25m.75-6.375C6.186 6.375 5.25 7.312 5.25 8.438V10.5c0 1.125.936 2.062 2.063 2.062H8.25v2.063c0 1.125.936 2.062 2.063 2.062H13.5c1.125 0 2.063-.937 2.063-2.062V14.812M12 12.75h1.5M12 12.75H7.5" />
  </svg>
);

const DocumentChartBarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 14.25V21a2.25 2.25 0 002.25 2.25h12a2.25 2.25 0 002.25-2.25V14.25M3.75 14.25H20.25M10.5 6H13.5M10.5 9H13.5M10.5 12H13.5" />
  </svg>
);

const DocumentMagnifyingGlassIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-slate-600" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5 4.5L6.75 17.25M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const CalendarDaysIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
  </svg>
);

const WrenchScrewdriverIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-slate-600" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.472-2.472a3.375 3.375 0 00-4.773-4.773L4.75 10.5M11.42 15.17L4.75 10.5m6.67 4.67l-2.472-2.472a3.375 3.375 0 00-4.773-4.773L4.75 10.5M11.42 15.17L4.75 10.5" />
  </svg>
);



const SidebarButton: React.FC<{ onClick: () => void; label: string; isActive: boolean; icon: React.ReactNode; badgeCount?: number }> = ({ onClick, label, isActive, icon, badgeCount }) => (
  <Button
    onClick={onClick}
    variant={isActive ? 'solid' : 'ghost'}
    className={`w-full flex items-center justify-start text-sm py-2.5 px-4 rounded-md transition-colors 
                ${isActive ? 'bg-[var(--color-primary)] text-white shadow-sm' 
                          : 'text-slate-700 hover:bg-[var(--color-primary)] hover:bg-opacity-10 hover:text-[var(--color-primary)]'}`}
    aria-current={isActive ? 'page' : undefined}
  >
    {icon}
    <span className="flex-grow">{label}</span>
    {typeof badgeCount === 'number' && badgeCount > 0 && (
      <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${isActive ? 'bg-white text-[var(--color-primary)]' : 'bg-[var(--color-accent)] text-black'}`}>
        {badgeCount}
      </span>
    )}
  </Button>
);

const AdminSubButton: React.FC<{ onClick: () => void; label: string; isActive: boolean; icon: React.ReactNode; }> = ({ onClick, label, isActive, icon }) => (
  <Button
    onClick={onClick}
    variant={isActive ? 'solid' : 'ghost'}
    className={`w-full flex items-center justify-start text-xs py-2 px-3 ml-4 rounded-md transition-colors
                ${isActive ? 'bg-[var(--color-accent)] bg-opacity-20 text-[var(--color-accent)]' 
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'}`}
    aria-current={isActive ? 'page' : undefined}
  >
    {icon}
    {label}
  </Button>
);


export const Sidebar: React.FC<SidebarProps> = ({
  activeView,
  newTicketsCount,
  onNewTicket,
  onShowDashboard,
  onShowAllTickets,
  onShowTicketBoard,
  onShowAppointments,
  onShowReports,
  onShowCustomerSearch,
  onShowInventory,
  onShowSelfCheckIn,
  onShowBuybackWidget,
  onShowLabels,
  onShowAdminUsers,
  onShowAdminSettings,
  onShowAdminUICustomization,
  onShowAdminActivityLog,
  onShowAdminBuybackSettings,
  // onShowAdminChecklists,
  onOpenSubmitSuggestion,
  onShowKnowledgeBase,
  onShowAITroubleshooter,
  onShowAIDiagnosticAssistant,
  currentUser
}) => {
  const isInactiveUser = currentUser?.status === 'Inactive';
  const isAdmin = currentUser?.role === 'Administrator';
  const isManager = currentUser?.role === 'Manager';
  const isLeadTech = currentUser?.role === 'Lead Technician';

  const canAccessReports = isAdmin || isManager || isLeadTech;
  const canAccessAdmin = isAdmin;

  return (
    <aside className="w-64 bg-white p-4 space-y-2 border-r border-slate-200 shadow-sm overflow-y-auto no-print">
      <Button
        onClick={onNewTicket}
        variant="secondary" // Use accent color
        className="w-full flex items-center justify-center text-sm !py-2.5 !px-4 mb-3 !text-black hover:!brightness-90"
        disabled={isInactiveUser}
        title={isInactiveUser ? "Inactive users cannot create tickets" : "Create a new service request"}
      >
        <PlusCircleIcon /> New Ticket
      </Button>

      <nav className="space-y-1">
        <SidebarButton onClick={onShowDashboard} label="Dashboard" isActive={activeView === 'dashboard'} icon={<HomeIcon />} />
        <SidebarButton onClick={onShowAllTickets} label="Service Queue" isActive={activeView === 'allTicketsView'} icon={<QueueIcon />} badgeCount={newTicketsCount} />
        <SidebarButton onClick={onShowTicketBoard} label="Ticket Board" isActive={activeView === 'ticketBoard'} icon={<ViewColumnsIcon />} />
        <SidebarButton onClick={onShowAppointments} label="Appointments" isActive={activeView === 'appointments'} icon={<CalendarDaysIcon />} />
        
        {canAccessReports && <SidebarButton onClick={onShowReports} label="Reports" isActive={activeView === 'reports'} icon={<DocumentChartBarIcon />} />}
        
        <SidebarButton onClick={onShowCustomerSearch} label="Customers" isActive={activeView === 'customerSearch' || activeView === 'customerProfile'} icon={<UserGroupIcon />} />
        <SidebarButton onClick={onShowInventory} label="Inventory" isActive={activeView === 'inventory'} icon={<ArchiveBoxIcon />} />
        <SidebarButton onClick={onShowLabels} label="Labels" isActive={activeView === 'labels'} icon={<TagIcon />} />
      </nav>

      <div className="pt-3">
        <h3 className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Kiosk Mode</h3>
        <nav className="space-y-1">
            <SidebarButton onClick={onShowSelfCheckIn} label="Self Check-in Kiosk" isActive={activeView === 'selfCheckIn'} icon={<ClipboardUserIcon />} />
            <SidebarButton onClick={onShowBuybackWidget} label="Buyback Quote Tool" isActive={activeView === 'buybackWidget'} icon={<CurrencyDollarIcon />} />
        </nav>
      </div>
      
      <div className="pt-3">
        <h3 className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">AI Tools</h3>
        <nav className="space-y-1">
            <SidebarButton onClick={onShowAITroubleshooter} label="AI Troubleshooter" isActive={activeView === 'aiTroubleshooter'} icon={<ChatBubbleLeftRightIcon />} />
            <SidebarButton onClick={onShowAIDiagnosticAssistant} label="Diagnostic Assistant" isActive={activeView === 'aiDiagnosticAssistant'} icon={<BeakerIcon />} />
        </nav>
      </div>

      {canAccessAdmin && (
        <div className="pt-3">
            <h3 className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Admin</h3>
            <nav className="space-y-1">
                <AdminSubButton onClick={onShowAdminUsers} label="Users" isActive={activeView === 'adminUsers'} icon={<UserCircleIconSmall />} />
                <AdminSubButton onClick={onShowAdminSettings} label="Settings" isActive={activeView === 'adminSettings'} icon={<CogIconSmall />} />
                <AdminSubButton onClick={onShowAdminUICustomization} label="UI Customization" isActive={activeView === 'adminUICustomization'} icon={<PaintBrushIcon />} />
                <AdminSubButton onClick={onShowAdminBuybackSettings} label="Buyback Program" isActive={activeView === 'adminBuybackSettings'} icon={<WrenchScrewdriverIcon />} />
                <AdminSubButton onClick={onShowAdminActivityLog} label="Activity Log" isActive={activeView === 'adminActivityLog'} icon={<DocumentMagnifyingGlassIcon />} />
                {/* {onShowAdminChecklists && <AdminSubButton onClick={onShowAdminChecklists} label="Checklists" isActive={activeView === 'adminChecklists'} icon={<ArrowDownOnSquareStackIcon />} />} */}
            </nav>
        </div>
      )}

      <div className="pt-3">
        <h3 className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Support Channels</h3>
        <nav className="space-y-1">
            <SidebarButton onClick={onOpenSubmitSuggestion} label="Submit Suggestion" isActive={false} icon={<LightBulbIcon />} />
            <SidebarButton onClick={onShowKnowledgeBase} label="Knowledge Base" isActive={activeView === 'knowledgeBase'} icon={<BookOpenIcon />} />
        </nav>
      </div>
    </aside>
  );
};
